import React, { useEffect, useState } from "react";
import Select from "react-select";
import { X } from "lucide-react";
import { toast } from "react-hot-toast";
import { selectStyles } from "../../helpers";
import { useCurrentDomain } from "../../context/DomainContext";
import { getMedicalNoteTemplateUsageDetails } from "../../services/masterDataService";

const pageSizeOptions = [
  { value: 25, label: "25 / page" },
  { value: 50, label: "50 / page" },
  { value: 100, label: "100 / page" },
];

const UsageDetailModal = ({ templateId, companyId, doctorId, doctorName, onClose }) => {
  const { domain } = useCurrentDomain();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(pageNumber, totalPages);

  const loadPage = async (page, size) => {
    setLoading(true);
    try {
      const data = await getMedicalNoteTemplateUsageDetails({
        domain,
        templateId,
        companyId,
        doctorId,
        pageNumber: page,
        pageSize: size,
      });
      setRows(data?.items || []);
      setTotalCount(data?.totalCount || 0);
      setPageNumber(page);
      setPageSize(size);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load usage details");
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(1, 25);
  }, [templateId, companyId, doctorId]);

  return (
    <div className="mntu-overlay mntu-overlay-detail" onClick={onClose}>
      <div className="mntu-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mntu-modal-header">
          <h3>{doctorName} ({doctorId}) — Visit</h3>
          <button type="button" className="mntu-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="mntu-modal-body">
          <table className="mntu-table">
            <thead>
              <tr>
                <th>Patient Name</th>
                <th>Patient Id</th>
                <th>Case Number</th>
                <th>Case Id</th>
                <th>Event Id</th>
                <th>Visit Date</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="mntu-empty">{loading ? "Loading..." : "No usage"}</td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.noteId}>
                    <td>{r.patientName}</td>
                    <td>{r.patientId}</td>
                    <td>{r.caseNumber}</td>
                    <td>{r.caseId}</td>
                    <td>{r.eventId}</td>
                    <td>{r.visitDate ? new Date(r.visitDate).toLocaleDateString("en-US") : ""}</td>
                    <td>{r.noteName}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalCount > 0 && (
          <div className="mntu-pager">
            <Select
              options={pageSizeOptions}
              value={pageSizeOptions.find((o) => o.value === pageSize)}
              onChange={(opt) => loadPage(1, opt.value)}
              styles={selectStyles}
              menuPlacement="top"
              isSearchable={false}
            />
            <span>
              {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, totalCount)} of {totalCount}
            </span>
            <button type="button" className="mntu-btn mntu-btn-reset" disabled={safePage <= 1} onClick={() => loadPage(safePage - 1, pageSize)}>Prev</button>
            <button type="button" className="mntu-btn mntu-btn-reset" disabled={safePage >= totalPages} onClick={() => loadPage(safePage + 1, pageSize)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageDetailModal;